import { useId, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

// Small charts for the start pages, drawn with HTML and SVG (no chart library). Rules from the dataviz guide:
// one axis, thin marks with 4px rounded ends on the baseline, a recessive grid, a tooltip on hover and keyboard
// focus, a legend for two or more series, and a table for screen readers. Colours are the --chart-* tokens, in
// fixed order (validated for colour blindness); text never takes the series colour.

export type ChartColor = 1 | 2 | 3;

const fill: Record<ChartColor, string> = { 1: 'bg-chart-1', 2: 'bg-chart-2', 3: 'bg-chart-3' };
const stroke: Record<ChartColor, string> = { 1: 'var(--chart-1)', 2: 'var(--chart-2)', 3: 'var(--chart-3)' };
const border: Record<ChartColor, string> = { 1: 'border-chart-1', 2: 'border-chart-2', 3: 'border-chart-3' };

export interface ChartPoint {
  /** Axis label, e.g. "okt" */
  label: string;
  /** Tooltip heading; defaults to the label, e.g. "oktober 2026" */
  fullLabel?: string;
  value: number;
}

const plain = (value: number) => String(value);

/** A round axis top whose half is a whole number too: 2, 4, 6, 8 or 10 times a power of ten */
function niceMax(value: number) {
  if (value <= 2) return 2;
  const power = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 4, 6, 8, 10].find((n) => n * power >= value) ?? 10;
  return Math.max(step * power, 2);
}

function ScreenReaderTable({ caption, rows }: { caption: string; rows: [string, string][] }) {
  return (
    <table className="sr-only">
      <caption>{caption}</caption>
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Tooltip({ title, children, left }: { title: string; children: ReactNode; left: string }) {
  return (
    <div
      role="presentation"
      className="pointer-events-none absolute bottom-full z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs shadow-md"
      style={{ left }}
    >
      <p className="font-medium text-muted-foreground">{title}</p>
      {children}
    </div>
  );
}

function Grid({ max, format }: { max: number; format: (value: number) => string }) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {[1, 0.5, 0].map((share) => (
        <div
          key={share}
          className={cn(
            'absolute inset-x-0 border-t',
            share === 0 ? 'border-border' : 'border-border/60 border-dashed',
          )}
          style={{ top: `${(1 - share) * 100}%` }}
        >
          {share > 0 && (
            <span className="absolute -top-2 right-full mr-2 text-[0.6875rem] tabular-nums text-muted-foreground">
              {format(max * share)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

interface BarChartProps {
  /** Names the chart for screen readers and the table */
  title: string;
  data: ChartPoint[];
  color?: ChartColor;
  format?: (value: number) => string;
  /** Plot height in px */
  height?: number;
  className?: string;
}

/** Vertical bars on one axis, e.g. proposals per month. The last bar (the current period) is full colour. */
export function BarChart({ title, data, color = 1, format = plain, height = 160, className }: BarChartProps) {
  const [active, setActive] = useState<number | null>(null);
  const max = niceMax(Math.max(...data.map((point) => point.value)));
  return (
    <figure className={cn('grid gap-2', className)}>
      <div className="relative ml-8" style={{ height }} onMouseLeave={() => setActive(null)}>
        <Grid max={max} format={format} />
        <div className="absolute inset-0 flex items-end gap-1.5 sm:gap-2.5">
          {data.map((point, index) => {
            const last = index === data.length - 1;
            return (
              <button
                key={point.label}
                type="button"
                aria-label={`${point.fullLabel ?? point.label}: ${format(point.value)}`}
                className="group relative flex h-full flex-1 items-end justify-center rounded-sm focus-visible:outline-2 focus-visible:outline-ring"
                onMouseEnter={() => setActive(index)}
                onFocus={() => setActive(index)}
                onBlur={() => setActive(null)}
              >
                <span
                  className={cn(
                    'w-full max-w-9 rounded-t-[4px] transition-opacity',
                    fill[color],
                    !last && 'opacity-55',
                    active === index && 'opacity-100',
                  )}
                  style={{ height: `${Math.max((point.value / max) * 100, point.value > 0 ? 2 : 0)}%` }}
                />
                {active === index && (
                  <Tooltip title={point.fullLabel ?? point.label} left="50%">
                    <p className="font-semibold tabular-nums">{format(point.value)}</p>
                  </Tooltip>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div aria-hidden className="ml-8 flex gap-1.5 sm:gap-2.5">
        {data.map((point) => (
          <span
            key={point.label}
            className="flex-1 truncate text-center text-[0.6875rem] text-muted-foreground"
          >
            {point.label}
          </span>
        ))}
      </div>
      <ScreenReaderTable caption={title} rows={data.map((p) => [p.fullLabel ?? p.label, format(p.value)])} />
    </figure>
  );
}

export interface ChartSeries {
  name: string;
  color: ChartColor;
  values: number[];
}

interface LineChartProps {
  title: string;
  /** The x axis, one label per value */
  labels: { label: string; fullLabel?: string }[];
  series: ChartSeries[];
  format?: (value: number) => string;
  height?: number;
  /** Show the legend above the chart (default); pass false when a ChartLegend sits elsewhere */
  legend?: boolean;
  className?: string;
}

/** One or more series over time on one axis, with a soft area under the first. Hover shows every series. */
/** The line colours and names; LineChart shows it on top unless `legend={false}`, e.g. to put it beside a title */
export function ChartLegend({
  series,
  className,
}: {
  series: { name: string; color: ChartColor }[];
  className?: string;
}) {
  return (
    <ul className={cn('flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground', className)}>
      {series.map((s) => (
        <li key={s.name} className="inline-flex items-center gap-1.5">
          <span aria-hidden className={cn('h-0.5 w-3 rounded-full', fill[s.color])} />
          {s.name}
        </li>
      ))}
    </ul>
  );
}

/** A monotone cubic path through the points (Fritsch–Carlson) */
function smoothPath(points: readonly (readonly [number, number])[]): string {
  if (points.length < 3) return points.map(([px, py], i) => `${i === 0 ? 'M' : 'L'}${px},${py}`).join(' ');
  const slopes = points.slice(1).map(([px, py], i) => (py - points[i]![1]) / (px - points[i]![0] || 1));
  const tangents = points.map((_, i) => {
    if (i === 0) return slopes[0]!;
    if (i === points.length - 1) return slopes[i - 1]!;
    const a = slopes[i - 1]!;
    const b = slopes[i]!;
    return a * b <= 0 ? 0 : (2 * a * b) / (a + b);
  });
  let d = `M${points[0]![0]},${points[0]![1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = points[i]!;
    const [x1, y1] = points[i + 1]!;
    const step = (x1 - x0) / 3;
    d += ` C${x0 + step},${y0 + tangents[i]! * step} ${x1 - step},${y1 - tangents[i + 1]! * step} ${x1},${y1}`;
  }
  return d;
}

export function LineChart({
  title,
  labels,
  series,
  format = plain,
  height = 160,
  legend = true,
  className,
}: LineChartProps) {
  const [active, setActive] = useState<number | null>(null);
  const gradientId = useId();
  const max = niceMax(Math.max(...series.flatMap((s) => s.values)));
  const count = labels.length;
  const x = (index: number) => (count === 1 ? 50 : (index / (count - 1)) * 100);
  const y = (value: number) => 100 - (value / max) * 100;
  // Smooth curves (monotone, so a line never overshoots below 0 or above its neighbours)
  const path = (values: number[]) => smoothPath(values.map((v, i) => [x(i), y(v)] as const));
  const first = series[0];

  return (
    <figure className={cn('grid gap-2', className)}>
      {legend && series.length > 1 && <ChartLegend series={series} />}
      <div className="relative ml-8" style={{ height }} onMouseLeave={() => setActive(null)}>
        <Grid max={max} format={format} />
        <svg
          aria-hidden
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 size-full overflow-visible"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={first ? stroke[first.color] : 'transparent'} stopOpacity="0.18" />
              <stop offset="100%" stopColor={first ? stroke[first.color] : 'transparent'} stopOpacity="0" />
            </linearGradient>
          </defs>
          {first && (
            <path
              d={`${path(first.values)} L${x(count - 1)},100 L${x(0)},100 Z`}
              fill={`url(#${gradientId})`}
            />
          )}
          {series.map((s) => (
            <path
              key={s.name}
              d={path(s.values)}
              fill="none"
              stroke={stroke[s.color]}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {active !== null && (
            <line
              x1={x(active)}
              x2={x(active)}
              y1="0"
              y2="100"
              stroke="var(--secondary)"
              strokeWidth="1"
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
        {active !== null &&
          series.map((s) => (
            <span
              key={s.name}
              aria-hidden
              className={cn(
                'absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-surface',
                border[s.color],
              )}
              style={{ left: `${x(active)}%`, top: `${y(s.values[active] ?? 0)}%` }}
            />
          ))}
        <div className="absolute inset-0 flex">
          {labels.map((label, index) => (
            <button
              key={label.label}
              type="button"
              aria-label={`${label.fullLabel ?? label.label}: ${series.map((s) => `${s.name} ${format(s.values[index] ?? 0)}`).join(', ')}`}
              className="h-full flex-1 focus-visible:outline-2 focus-visible:outline-ring"
              onMouseEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onBlur={() => setActive(null)}
            />
          ))}
        </div>
        {active !== null && (
          <Tooltip title={labels[active]?.fullLabel ?? labels[active]?.label ?? ''} left={`${x(active)}%`}>
            {series.map((s) => (
              <p key={s.name} className="flex items-center gap-1.5 tabular-nums">
                <span aria-hidden className={cn('size-2 rounded-full', fill[s.color])} />
                {series.length > 1 && <span className="text-muted-foreground">{s.name}</span>}
                <span className="font-semibold">{format(s.values[active] ?? 0)}</span>
              </p>
            ))}
          </Tooltip>
        )}
      </div>
      <div aria-hidden className="ml-8 flex justify-between">
        {labels.map((label) => (
          <span key={label.label} className="text-[0.6875rem] text-muted-foreground">
            {label.label}
          </span>
        ))}
      </div>
      <ScreenReaderTable
        caption={title}
        rows={labels.map((label, index) => [
          label.fullLabel ?? label.label,
          series.map((s) => `${s.name}: ${format(s.values[index] ?? 0)}`).join(', '),
        ])}
      />
    </figure>
  );
}

export interface DonutSegment {
  label: string;
  value: number;
  color: ChartColor | 'muted';
}

interface DonutChartProps {
  title: string;
  segments: DonutSegment[];
  /** Big number in the middle, e.g. the total */
  centerValue?: ReactNode;
  centerLabel?: string;
  format?: (value: number) => string;
  className?: string;
}

const RADIUS = 15.9155; // circumference 100, so dash lengths are percentages
const segmentStroke = (color: DonutSegment['color']) => (color === 'muted' ? 'var(--input)' : stroke[color]);

/** Parts of a whole (2–4 parts), with a legend that names every part and its value. */
export function DonutChart({
  title,
  segments,
  centerValue,
  centerLabel,
  format = plain,
  className,
}: DonutChartProps) {
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  const visible = segments.filter((segment) => segment.value > 0);
  // A 1-unit surface gap between parts, unless one part fills the ring
  const gap = visible.length > 1 ? 1 : 0;
  let start = 0;

  return (
    <figure className={cn('flex flex-wrap items-center gap-5', className)}>
      <div className="relative size-32 shrink-0">
        <svg aria-hidden viewBox="0 0 36 36" className="size-full -rotate-90">
          <circle cx="18" cy="18" r={RADIUS} fill="none" stroke="var(--muted)" strokeWidth="4" />
          {total > 0 &&
            visible.map((segment) => {
              const share = (segment.value / total) * 100;
              const dash = Math.max(share - gap, 0.5);
              const circle = (
                <circle
                  key={segment.label}
                  cx="18"
                  cy="18"
                  r={RADIUS}
                  fill="none"
                  stroke={segmentStroke(segment.color)}
                  strokeWidth="4"
                  strokeDasharray={`${dash} ${100 - dash}`}
                  strokeDashoffset={-(start + gap / 2)}
                />
              );
              start += share;
              return circle;
            })}
        </svg>
        {(centerValue !== undefined || centerLabel) && (
          <div aria-hidden className="absolute inset-0 grid place-content-center text-center">
            {centerValue !== undefined && (
              <span className="text-xl font-bold tabular-nums">{centerValue}</span>
            )}
            {centerLabel && <span className="text-[0.6875rem] text-muted-foreground">{centerLabel}</span>}
          </div>
        )}
      </div>
      <ul aria-hidden className="grid min-w-36 flex-1 gap-1.5 text-sm">
        {segments.map((segment) => (
          <li key={segment.label} className="flex items-center gap-2">
            <span
              className={cn(
                'size-2.5 shrink-0 rounded-full',
                segment.color === 'muted' ? 'bg-input' : fill[segment.color],
              )}
            />
            <span className="flex-1 truncate text-muted-foreground">{segment.label}</span>
            <span className="font-semibold tabular-nums">{format(segment.value)}</span>
          </li>
        ))}
      </ul>
      <ScreenReaderTable caption={title} rows={segments.map((s) => [s.label, format(s.value)])} />
    </figure>
  );
}

interface ProgressRingProps {
  /** Read out with the value, e.g. "Credits used" */
  label: string;
  value: number;
  max: number;
  /** Text in the middle; defaults to the percentage */
  center?: ReactNode;
  size?: 'sm' | 'md';
  color?: ChartColor;
  className?: string;
}

/** One value against its limit, e.g. proposals used this month. */
export function ProgressRing({
  label,
  value,
  max,
  center,
  size = 'md',
  color = 1,
  className,
}: ProgressRingProps) {
  const share = max > 0 ? Math.min(value / max, 1) * 100 : 0;
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      className={cn('relative shrink-0', size === 'sm' ? 'size-16' : 'size-28', className)}
    >
      <svg aria-hidden viewBox="0 0 36 36" className="size-full -rotate-90">
        <circle cx="18" cy="18" r={RADIUS} fill="none" stroke="var(--muted)" strokeWidth="3.5" />
        {share > 0 && (
          <circle
            cx="18"
            cy="18"
            r={RADIUS}
            fill="none"
            stroke={stroke[color]}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray={`${share} ${100 - share}`}
          />
        )}
      </svg>
      <span
        aria-hidden
        className={cn(
          'absolute inset-0 grid place-content-center font-bold tabular-nums',
          size === 'sm' ? 'text-sm' : 'text-lg',
        )}
      >
        {center ?? `${Math.round(share)}%`}
      </span>
    </div>
  );
}

/** A tiny trend line next to a number; decorative, so the number must say it all. */
export function Sparkline({
  values,
  color = 1,
  className,
}: {
  values: number[];
  color?: ChartColor;
  className?: string;
}) {
  const max = Math.max(...values, 1);
  const points = values
    .map(
      (value, index) =>
        `${values.length === 1 ? 50 : (index / (values.length - 1)) * 100},${100 - (value / max) * 90 - 5}`,
    )
    .join(' ');
  return (
    <svg aria-hidden viewBox="0 0 100 100" preserveAspectRatio="none" className={cn('h-8 w-20', className)}>
      <polyline
        points={points}
        fill="none"
        stroke={stroke[color]}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
